/**
 * Procedural Audio Manager — realistic sounds via Web Audio API.
 * Multi-layered synthesis, reverb, formant shaping.
 */
export class AudioManager {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private bgmGain: GainNode | null = null;
  private seGain: GainNode | null = null;
  private reverb: ConvolverNode | null = null;
  private reverbGain: GainNode | null = null;
  private activeBgm: { stop: () => void } | null = null;

  init(): void {
    if (this.ctx) return;
    this.ctx = new AudioContext();
    this.masterGain = this.ctx.createGain();
    this.masterGain.connect(this.ctx.destination);

    this.bgmGain = this.ctx.createGain();
    this.bgmGain.gain.value = 0.35;
    this.bgmGain.connect(this.masterGain);

    this.seGain = this.ctx.createGain();
    this.seGain.gain.value = 0.6;
    this.seGain.connect(this.masterGain);

    // Reverb (corridor echo)
    this.reverb = this.ctx.createConvolver();
    this.reverb.buffer = this.createReverbIR(1.8, 0.6);
    this.reverbGain = this.ctx.createGain();
    this.reverbGain.gain.value = 0.25;
    this.reverb.connect(this.reverbGain).connect(this.masterGain);
  }

  /** Generate impulse response for reverb */
  private createReverbIR(duration: number, decay: number): AudioBuffer {
    const ctx = this.ctx!;
    const len = ctx.sampleRate * duration;
    const buf = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const d = buf.getChannelData(ch);
      for (let i = 0; i < len; i++) {
        d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay * 3);
      }
    }
    return buf;
  }

  private ensure(): AudioContext {
    if (!this.ctx) this.init();
    if (this.ctx!.state === 'suspended') this.ctx!.resume();
    return this.ctx!;
  }

  /** Create shaped noise buffer */
  private noise(dur: number, decayRate = 0.15): AudioBufferSourceNode {
    const ctx = this.ctx!;
    const buf = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) {
      d[i] = (Math.random() * 2 - 1) * Math.exp(-i / (d.length * decayRate));
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    return src;
  }

  /** Route to SE output + reverb send */
  private toSE(node: AudioNode): void {
    node.connect(this.seGain!);
    if (this.reverb) node.connect(this.reverb);
  }

  setVolume(bgm: number, se: number): void {
    if (this.bgmGain) this.bgmGain.gain.value = bgm;
    if (this.seGain) this.seGain.gain.value = se;
  }

  // ==================== SOUND EFFECTS ====================

  /** Footstep — heel impact + floor resonance + scuff */
  playFootstep(): void {
    const ctx = this.ensure();
    const now = ctx.currentTime;
    const isLeft = Math.random() > 0.5;

    // Heel impact (low thud)
    const impact = ctx.createOscillator();
    impact.type = 'sine';
    impact.frequency.setValueAtTime(isLeft ? 90 : 100, now);
    impact.frequency.exponentialRampToValueAtTime(30, now + 0.06);
    const ig = ctx.createGain();
    ig.gain.setValueAtTime(0.25, now);
    ig.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
    impact.connect(ig);
    this.toSE(ig);
    impact.start(now);
    impact.stop(now + 0.08);

    // Tile click (high transient)
    const click = this.noise(0.03, 0.05);
    const clickFilt = ctx.createBiquadFilter();
    clickFilt.type = 'bandpass';
    clickFilt.frequency.value = 3000 + Math.random() * 1500;
    clickFilt.Q.value = 5;
    const cg = ctx.createGain();
    cg.gain.setValueAtTime(0.12, now + 0.005);
    cg.gain.exponentialRampToValueAtTime(0.001, now + 0.04);
    click.connect(clickFilt).connect(cg);
    this.toSE(cg);
    click.start(now + 0.005);
    click.stop(now + 0.04);

    // Scuff (late noise)
    const scuff = this.noise(0.05, 0.1);
    const sf = ctx.createBiquadFilter();
    sf.type = 'lowpass';
    sf.frequency.value = 1200;
    const sg = ctx.createGain();
    sg.gain.setValueAtTime(0.06, now + 0.03);
    sg.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
    scuff.connect(sf).connect(sg);
    this.toSE(sg);
    scuff.start(now + 0.03);
    scuff.stop(now + 0.1);
  }

  /** Door smash — wood splintering + metal bend + debris scatter + reverb tail */
  playDoorSmash(): void {
    const ctx = this.ensure();
    const now = ctx.currentTime;

    // 1. Initial impact boom (sub bass)
    const boom = ctx.createOscillator();
    boom.type = 'sine';
    boom.frequency.setValueAtTime(100, now);
    boom.frequency.exponentialRampToValueAtTime(15, now + 0.5);
    const bg = ctx.createGain();
    bg.gain.setValueAtTime(0.9, now);
    bg.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
    boom.connect(bg);
    this.toSE(bg);
    boom.start(now);
    boom.stop(now + 0.6);

    // 2. Wood cracking (pitched noise bursts)
    for (let i = 0; i < 3; i++) {
      const t = now + i * 0.04;
      const crack = this.noise(0.08, 0.06);
      const cf = ctx.createBiquadFilter();
      cf.type = 'bandpass';
      cf.frequency.value = 1500 + Math.random() * 2000;
      cf.Q.value = 4 + Math.random() * 3;
      const cg = ctx.createGain();
      cg.gain.setValueAtTime(0.4, t);
      cg.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
      crack.connect(cf).connect(cg);
      this.toSE(cg);
      crack.start(t);
      crack.stop(t + 0.1);
    }

    // 3. Metal rattle/groan
    const metal1 = ctx.createOscillator();
    metal1.type = 'sawtooth';
    metal1.frequency.setValueAtTime(220, now + 0.05);
    metal1.frequency.exponentialRampToValueAtTime(80, now + 0.8);
    const mf = ctx.createBiquadFilter();
    mf.type = 'bandpass';
    mf.frequency.value = 800;
    mf.Q.value = 8;
    const mg = ctx.createGain();
    mg.gain.setValueAtTime(0.3, now + 0.05);
    mg.gain.exponentialRampToValueAtTime(0.001, now + 0.8);
    metal1.connect(mf).connect(mg);
    this.toSE(mg);
    metal1.start(now + 0.05);
    metal1.stop(now + 0.8);

    // 4. Debris scatter (multiple small clicks/tinkles)
    for (let i = 0; i < 6; i++) {
      const t = now + 0.1 + Math.random() * 0.5;
      const deb = this.noise(0.04, 0.05);
      const df = ctx.createBiquadFilter();
      df.type = 'highpass';
      df.frequency.value = 2000 + Math.random() * 4000;
      const dg = ctx.createGain();
      dg.gain.setValueAtTime(0.08 + Math.random() * 0.1, t);
      dg.gain.exponentialRampToValueAtTime(0.001, t + 0.06);
      deb.connect(df).connect(dg);
      this.toSE(dg);
      deb.start(t);
      deb.stop(t + 0.06);
    }

    // 5. Resonant echo boom
    const echo = ctx.createOscillator();
    echo.type = 'sine';
    echo.frequency.value = 40;
    const eg = ctx.createGain();
    eg.gain.setValueAtTime(0.3, now + 0.15);
    eg.gain.exponentialRampToValueAtTime(0.001, now + 1.2);
    echo.connect(eg);
    this.toSE(eg);
    echo.start(now + 0.15);
    echo.stop(now + 1.2);
  }

  /** Glass break — initial shatter + falling shards tinkle */
  playGlassBreak(): void {
    const ctx = this.ensure();
    const now = ctx.currentTime;

    // Initial shatter (broad noise)
    const shatter = this.noise(0.15, 0.08);
    const sf = ctx.createBiquadFilter();
    sf.type = 'highpass';
    sf.frequency.value = 2000;
    const sg = ctx.createGain();
    sg.gain.setValueAtTime(0.45, now);
    sg.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
    shatter.connect(sf).connect(sg);
    this.toSE(sg);
    shatter.start(now);
    shatter.stop(now + 0.2);

    // Falling shards (multiple resonant pings)
    for (let i = 0; i < 10; i++) {
      const t = now + 0.05 + Math.random() * 0.6;
      const freq = 3000 + Math.random() * 6000;
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const og = ctx.createGain();
      const vol = 0.03 + Math.random() * 0.06;
      og.gain.setValueAtTime(vol, t);
      og.gain.exponentialRampToValueAtTime(0.001, t + 0.04 + Math.random() * 0.08);
      osc.connect(og);
      this.toSE(og);
      osc.start(t);
      osc.stop(t + 0.12);
    }
  }

  /** Zombie growl — multi-formant vocal synthesis + gurgling */
  playZombieGrowl(): void {
    const ctx = this.ensure();
    const now = ctx.currentTime;
    const dur = 1.5;
    const baseFreq = 65 + Math.random() * 25;

    // Vocal cord (sawtooth for harmonic-rich source)
    const vocal = ctx.createOscillator();
    vocal.type = 'sawtooth';
    vocal.frequency.setValueAtTime(baseFreq, now);
    vocal.frequency.linearRampToValueAtTime(baseFreq * 0.7, now + dur);

    // Jitter (irregular pitch wobble — inhuman quality)
    const jitter = ctx.createOscillator();
    jitter.frequency.value = 5 + Math.random() * 8;
    const jitterG = ctx.createGain();
    jitterG.gain.value = 8 + Math.random() * 5;
    jitter.connect(jitterG).connect(vocal.frequency);
    jitter.start(now);
    jitter.stop(now + dur);

    // Formant 1 (throat — low)
    const f1 = ctx.createBiquadFilter();
    f1.type = 'bandpass';
    f1.frequency.value = 300 + Math.random() * 100;
    f1.Q.value = 5;

    // Formant 2 (mouth — mid)
    const f2 = ctx.createBiquadFilter();
    f2.type = 'bandpass';
    f2.frequency.value = 800 + Math.random() * 300;
    f2.Q.value = 6;

    // Gurgle (amplitude modulation — bubbling)
    const gurgleLFO = ctx.createOscillator();
    gurgleLFO.frequency.value = 12 + Math.random() * 10;
    const gurgleAmt = ctx.createGain();
    gurgleAmt.gain.value = 0.4;
    gurgleLFO.connect(gurgleAmt);
    gurgleLFO.start(now);
    gurgleLFO.stop(now + dur);

    const vocalGain = ctx.createGain();
    gurgleAmt.connect(vocalGain.gain);
    vocalGain.gain.setValueAtTime(0, now);
    vocalGain.gain.linearRampToValueAtTime(0.3, now + 0.15);
    vocalGain.gain.setValueAtTime(0.3, now + dur * 0.3);
    vocalGain.gain.linearRampToValueAtTime(0.2, now + dur * 0.8);
    vocalGain.gain.exponentialRampToValueAtTime(0.001, now + dur);

    // Breath noise layer
    const breath = this.noise(dur, 0.5);
    const breathFilt = ctx.createBiquadFilter();
    breathFilt.type = 'bandpass';
    breathFilt.frequency.value = 600;
    breathFilt.Q.value = 2;
    const breathGain = ctx.createGain();
    breathGain.gain.setValueAtTime(0, now);
    breathGain.gain.linearRampToValueAtTime(0.1, now + 0.2);
    breathGain.gain.exponentialRampToValueAtTime(0.001, now + dur);

    // Route formants in parallel
    const mix = ctx.createGain();
    mix.gain.value = 1;
    vocal.connect(f1).connect(mix);
    vocal.connect(f2).connect(mix);
    mix.connect(vocalGain);
    this.toSE(vocalGain);

    breath.connect(breathFilt).connect(breathGain);
    this.toSE(breathGain);

    vocal.start(now);
    vocal.stop(now + dur);
    breath.start(now);
    breath.stop(now + dur);
  }

  /** Scream — gasp intake → cry → trembling voice → choke off */
  playScream(): void {
    const ctx = this.ensure();
    const now = ctx.currentTime;

    // === Phase 1: Sharp inhale gasp (0 - 0.15s) ===
    const gasp = this.noise(0.15, 0.08);
    const gaspF1 = ctx.createBiquadFilter();
    gaspF1.type = 'bandpass'; gaspF1.frequency.value = 1800; gaspF1.Q.value = 3;
    const gaspF2 = ctx.createBiquadFilter();
    gaspF2.type = 'highpass'; gaspF2.frequency.value = 800;
    const gaspG = ctx.createGain();
    gaspG.gain.setValueAtTime(0, now);
    gaspG.gain.linearRampToValueAtTime(0.2, now + 0.03);
    gaspG.gain.linearRampToValueAtTime(0.15, now + 0.1);
    gaspG.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
    gasp.connect(gaspF1).connect(gaspF2).connect(gaspG);
    this.toSE(gaspG);
    gasp.start(now);
    gasp.stop(now + 0.18);

    // === Phase 2: Main scream cry (0.12 - 0.9s) ===
    const screamStart = now + 0.12;
    const screamDur = 0.8;

    // Voiced source (dual oscillators for richness)
    const voice1 = ctx.createOscillator();
    voice1.type = 'sawtooth';
    voice1.frequency.setValueAtTime(250, screamStart);
    voice1.frequency.linearRampToValueAtTime(520, screamStart + 0.06); // pitch shoots up
    voice1.frequency.linearRampToValueAtTime(480, screamStart + 0.25);
    voice1.frequency.linearRampToValueAtTime(400, screamStart + 0.5);
    voice1.frequency.linearRampToValueAtTime(320, screamStart + screamDur);

    const voice2 = ctx.createOscillator();
    voice2.type = 'sawtooth';
    voice2.frequency.setValueAtTime(253, screamStart); // slight detune for thickness
    voice2.frequency.linearRampToValueAtTime(525, screamStart + 0.06);
    voice2.frequency.linearRampToValueAtTime(483, screamStart + 0.25);
    voice2.frequency.linearRampToValueAtTime(403, screamStart + 0.5);
    voice2.frequency.linearRampToValueAtTime(322, screamStart + screamDur);

    // Vibrato (intensifies over time — trembling fear)
    const vib = ctx.createOscillator();
    vib.frequency.setValueAtTime(4, screamStart);
    vib.frequency.linearRampToValueAtTime(8, screamStart + screamDur); // faster trembling
    const vibAmt = ctx.createGain();
    vibAmt.gain.setValueAtTime(5, screamStart);
    vibAmt.gain.linearRampToValueAtTime(25, screamStart + screamDur); // wider wobble
    vib.connect(vibAmt);
    vibAmt.connect(voice1.frequency);
    vibAmt.connect(voice2.frequency);
    vib.start(screamStart);
    vib.stop(screamStart + screamDur);

    // Formant chain — vowel transition: "AH" → "EH" → choke
    // F1: 700→500 (open→closing)
    const f1 = ctx.createBiquadFilter();
    f1.type = 'bandpass'; f1.Q.value = 6;
    f1.frequency.setValueAtTime(750, screamStart);
    f1.frequency.linearRampToValueAtTime(600, screamStart + 0.5);
    f1.frequency.linearRampToValueAtTime(400, screamStart + screamDur);

    // F2: 1100→1500 (bright cry)
    const f2 = ctx.createBiquadFilter();
    f2.type = 'bandpass'; f2.Q.value = 5;
    f2.frequency.setValueAtTime(1100, screamStart);
    f2.frequency.linearRampToValueAtTime(1500, screamStart + 0.3);
    f2.frequency.linearRampToValueAtTime(1200, screamStart + screamDur);

    // F3: nasal resonance
    const f3 = ctx.createBiquadFilter();
    f3.type = 'bandpass'; f3.Q.value = 4;
    f3.frequency.setValueAtTime(2800, screamStart);
    f3.frequency.linearRampToValueAtTime(2400, screamStart + screamDur);

    // F4: presence / edge
    const f4 = ctx.createBiquadFilter();
    f4.type = 'bandpass'; f4.Q.value = 3;
    f4.frequency.value = 3500;

    // Mix formants in parallel
    const fMix = ctx.createGain();
    fMix.gain.value = 0.7;
    const voiceMix = ctx.createGain();
    voice1.connect(voiceMix);
    voice2.connect(voiceMix);
    voiceMix.connect(f1).connect(fMix);
    voiceMix.connect(f2).connect(fMix);
    voiceMix.connect(f3).connect(fMix);
    voiceMix.connect(f4).connect(fMix);

    // Volume envelope — sudden burst, holds, then chokes off
    const screamEnv = ctx.createGain();
    screamEnv.gain.setValueAtTime(0, screamStart);
    screamEnv.gain.linearRampToValueAtTime(0.28, screamStart + 0.03);
    screamEnv.gain.setValueAtTime(0.28, screamStart + 0.08);
    screamEnv.gain.linearRampToValueAtTime(0.22, screamStart + 0.35);
    screamEnv.gain.linearRampToValueAtTime(0.18, screamStart + 0.55);
    // Choke (voice breaking)
    screamEnv.gain.linearRampToValueAtTime(0.08, screamStart + 0.7);
    screamEnv.gain.exponentialRampToValueAtTime(0.001, screamStart + screamDur);

    fMix.connect(screamEnv);
    this.toSE(screamEnv);

    voice1.start(screamStart); voice1.stop(screamStart + screamDur);
    voice2.start(screamStart); voice2.stop(screamStart + screamDur);

    // === Layer: Breathy turbulence (air rushing past vocal cords) ===
    const breathNoise = this.noise(screamDur, 0.4);
    const bnF1 = ctx.createBiquadFilter();
    bnF1.type = 'bandpass'; bnF1.frequency.value = 1200; bnF1.Q.value = 1.5;
    const bnF2 = ctx.createBiquadFilter();
    bnF2.type = 'highpass'; bnF2.frequency.value = 600;
    const bnG = ctx.createGain();
    bnG.gain.setValueAtTime(0, screamStart);
    bnG.gain.linearRampToValueAtTime(0.08, screamStart + 0.05);
    bnG.gain.linearRampToValueAtTime(0.05, screamStart + 0.5);
    bnG.gain.exponentialRampToValueAtTime(0.001, screamStart + screamDur);
    breathNoise.connect(bnF1).connect(bnF2).connect(bnG);
    this.toSE(bnG);
    breathNoise.start(screamStart); breathNoise.stop(screamStart + screamDur);

    // === Phase 3: Whimper / sob trailing off (0.85 - 1.2s) ===
    const sobStart = screamStart + screamDur - 0.1;
    const sobDur = 0.4;
    const sob = ctx.createOscillator();
    sob.type = 'sawtooth';
    sob.frequency.setValueAtTime(300, sobStart);
    sob.frequency.linearRampToValueAtTime(220, sobStart + sobDur);
    const sobVib = ctx.createOscillator();
    sobVib.frequency.value = 10; // fast trembling
    const sobVibG = ctx.createGain();
    sobVibG.gain.value = 20;
    sobVib.connect(sobVibG).connect(sob.frequency);
    sobVib.start(sobStart); sobVib.stop(sobStart + sobDur);

    const sobF = ctx.createBiquadFilter();
    sobF.type = 'bandpass'; sobF.frequency.value = 500; sobF.Q.value = 5;
    const sobG = ctx.createGain();
    sobG.gain.setValueAtTime(0.06, sobStart);
    sobG.gain.exponentialRampToValueAtTime(0.001, sobStart + sobDur);
    sob.connect(sobF).connect(sobG);
    this.toSE(sobG);
    sob.start(sobStart); sob.stop(sobStart + sobDur);
  }

  /** Punch impact — bone crunch + flesh slap + breath impact */
  playPunch(): void {
    const ctx = this.ensure();
    const now = ctx.currentTime;

    // 1. Bone crack transient
    const crack = this.noise(0.02, 0.03);
    const cf = ctx.createBiquadFilter();
    cf.type = 'bandpass'; cf.frequency.value = 2500; cf.Q.value = 3;
    const cg = ctx.createGain();
    cg.gain.setValueAtTime(0.5, now);
    cg.gain.exponentialRampToValueAtTime(0.001, now + 0.03);
    crack.connect(cf).connect(cg);
    this.toSE(cg);
    crack.start(now); crack.stop(now + 0.03);

    // 2. Flesh impact (low thud)
    const thud = ctx.createOscillator();
    thud.type = 'sine';
    thud.frequency.setValueAtTime(120, now);
    thud.frequency.exponentialRampToValueAtTime(25, now + 0.12);
    const tg = ctx.createGain();
    tg.gain.setValueAtTime(0.7, now);
    tg.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
    thud.connect(tg);
    this.toSE(tg);
    thud.start(now); thud.stop(now + 0.2);

    // 3. Slap noise (mid-high)
    const slap = this.noise(0.05, 0.08);
    const sf = ctx.createBiquadFilter();
    sf.type = 'bandpass'; sf.frequency.value = 1200; sf.Q.value = 2;
    const sg = ctx.createGain();
    sg.gain.setValueAtTime(0.35, now + 0.005);
    sg.gain.exponentialRampToValueAtTime(0.001, now + 0.06);
    slap.connect(sf).connect(sg);
    this.toSE(sg);
    slap.start(now + 0.005); slap.stop(now + 0.07);

    // 4. Breath impact (short grunt)
    const grunt = ctx.createOscillator();
    grunt.type = 'sawtooth';
    grunt.frequency.setValueAtTime(180, now);
    grunt.frequency.exponentialRampToValueAtTime(80, now + 0.1);
    const gf = ctx.createBiquadFilter();
    gf.type = 'lowpass'; gf.frequency.value = 500;
    const gg = ctx.createGain();
    gg.gain.setValueAtTime(0.15, now + 0.01);
    gg.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
    grunt.connect(gf).connect(gg);
    this.toSE(gg);
    grunt.start(now + 0.01); grunt.stop(now + 0.15);
  }

  /** Body fall — sequential impacts: shoulder + torso + head bounce */
  playBodyFall(): void {
    const ctx = this.ensure();
    const now = ctx.currentTime;

    const impacts: [number, number, number][] = [
      [0, 70, 0.5],    // shoulder hits
      [0.08, 50, 0.4], // torso
      [0.2, 90, 0.25], // head bounce
    ];

    for (const [delay, freq, vol] of impacts) {
      const t = now + delay;
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, t);
      osc.frequency.exponentialRampToValueAtTime(15, t + 0.2);
      const g = ctx.createGain();
      g.gain.setValueAtTime(vol, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
      osc.connect(g);
      this.toSE(g);
      osc.start(t); osc.stop(t + 0.25);

      // Cloth rustle
      const cloth = this.noise(0.08, 0.1);
      const cf = ctx.createBiquadFilter();
      cf.type = 'bandpass'; cf.frequency.value = 2000; cf.Q.value = 1;
      const cg = ctx.createGain();
      cg.gain.setValueAtTime(0.1, t);
      cg.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
      cloth.connect(cf).connect(cg);
      this.toSE(cg);
      cloth.start(t); cloth.stop(t + 0.1);
    }
  }

  /** Gunshot — realistic sharp crack like real pistol */
  playGunshot(): void {
    const ctx = this.ensure();
    const now = ctx.currentTime;

    // Layer 1: Sharp initial crack (the "PAN" sound - very high frequency burst)
    const crack = this.noise(0.01, 0.01);
    const crackFilter = ctx.createBiquadFilter();
    crackFilter.type = 'highpass';
    crackFilter.frequency.value = 4000;
    const cg = ctx.createGain();
    cg.gain.setValueAtTime(0.9, now);
    cg.gain.linearRampToValueAtTime(0, now + 0.015);
    crack.connect(crackFilter).connect(cg);
    this.toSE(cg);
    crack.start(now); crack.stop(now + 0.015);

    // Layer 1b: Extra high snap for sharpness
    const snap = this.noise(0.005, 0.005);
    const snapFilter = ctx.createBiquadFilter();
    snapFilter.type = 'highpass';
    snapFilter.frequency.value = 6000;
    const sg = ctx.createGain();
    sg.gain.setValueAtTime(0.6, now);
    sg.gain.linearRampToValueAtTime(0, now + 0.008);
    snap.connect(snapFilter).connect(sg);
    this.toSE(sg);
    snap.start(now); snap.stop(now + 0.008);

    // Layer 2: Mid punch (body of the shot)
    const punch = this.noise(0.03, 0.03);
    const punchFilter = ctx.createBiquadFilter();
    punchFilter.type = 'bandpass';
    punchFilter.frequency.value = 800;
    punchFilter.Q.value = 1.5;
    const pg = ctx.createGain();
    pg.gain.setValueAtTime(0.7, now);
    pg.gain.exponentialRampToValueAtTime(0.01, now + 0.05);
    punch.connect(punchFilter).connect(pg);
    this.toSE(pg);
    punch.start(now); punch.stop(now + 0.05);

    // Layer 3: Low thump (gunpowder explosion feel)
    const thump = ctx.createOscillator();
    thump.type = 'sine';
    thump.frequency.setValueAtTime(120, now);
    thump.frequency.exponentialRampToValueAtTime(20, now + 0.06);
    const tg = ctx.createGain();
    tg.gain.setValueAtTime(0.5, now);
    tg.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
    thump.connect(tg);
    this.toSE(tg);
    thump.start(now); thump.stop(now + 0.08);

    // Layer 4: Tail echo (reverb/ring after shot)
    const tail = this.noise(0.08, 0.1);
    const tailFilter = ctx.createBiquadFilter();
    tailFilter.type = 'bandpass';
    tailFilter.frequency.value = 1200;
    tailFilter.Q.value = 0.8;
    const tgTail = ctx.createGain();
    tgTail.gain.setValueAtTime(0.15, now + 0.02);
    tgTail.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
    tail.connect(tailFilter).connect(tgTail);
    this.toSE(tgTail);
    tail.start(now + 0.02); tail.stop(now + 0.12);

    // Layer 5: Mechanical click (slide/bolt action)
    const mech = this.noise(0.01, 0.01);
    const mechFilter = ctx.createBiquadFilter();
    mechFilter.type = 'highpass';
    mechFilter.frequency.value = 4000;
    const mechGain = ctx.createGain();
    mechGain.gain.setValueAtTime(0.12, now + 0.04);
    mechGain.gain.exponentialRampToValueAtTime(0.001, now + 0.06);
    mech.connect(mechFilter).connect(mechGain);
    this.toSE(mechGain);
    mech.start(now + 0.04); mech.stop(now + 0.06);
  }

  /** Key pickup — ascending chime with shimmer */
  playKeyPickup(): void {
    const ctx = this.ensure();
    const now = ctx.currentTime;
    const notes = [880, 1100, 1320, 1760];
    notes.forEach((freq, i) => {
      const t = now + i * 0.07;
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      // Shimmer: slight detune partner
      const osc2 = ctx.createOscillator();
      osc2.type = 'sine';
      osc2.frequency.value = freq * 1.005;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.15, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
      osc.connect(g);
      osc2.connect(g);
      this.toSE(g);
      osc.start(t); osc2.start(t);
      osc.stop(t + 0.4); osc2.stop(t + 0.4);
    });
  }

  /** Door unlock — mechanical bolt sequence */
  playDoorUnlock(): void {
    const ctx = this.ensure();
    const now = ctx.currentTime;

    // Key turn (metal scrape)
    const scrape = this.noise(0.15, 0.12);
    const sf = ctx.createBiquadFilter();
    sf.type = 'bandpass'; sf.frequency.value = 2000; sf.Q.value = 4;
    const sg = ctx.createGain();
    sg.gain.setValueAtTime(0.15, now);
    sg.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
    scrape.connect(sf).connect(sg);
    this.toSE(sg);
    scrape.start(now); scrape.stop(now + 0.15);

    // Bolt clicks (2 distinct)
    for (let i = 0; i < 2; i++) {
      const t = now + 0.1 + i * 0.12;
      const click = ctx.createOscillator();
      click.type = 'square';
      click.frequency.value = 1800 + i * 600;
      const cg = ctx.createGain();
      cg.gain.setValueAtTime(0.25, t);
      cg.gain.exponentialRampToValueAtTime(0.001, t + 0.03);
      click.connect(cg);
      this.toSE(cg);
      click.start(t); click.stop(t + 0.04);
    }

    // Heavy bolt sliding
    const slide = this.noise(0.2, 0.15);
    const slF = ctx.createBiquadFilter();
    slF.type = 'lowpass'; slF.frequency.value = 600;
    const slG = ctx.createGain();
    slG.gain.setValueAtTime(0.2, now + 0.25);
    slG.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
    slide.connect(slF).connect(slG);
    this.toSE(slG);
    slide.start(now + 0.25); slide.stop(now + 0.45);
  }

  // ==================== ALARM ====================

  /** Emergency alarm — loud, realistic facility alarm */
  startAlarm(): { stop: () => void } {
    const ctx = this.ensure();
    const now = ctx.currentTime;
    const allNodes: OscillatorNode[] = [];
    const allGains: GainNode[] = [];

    // === Main siren (two-tone, loud) ===
    const siren = ctx.createOscillator();
    siren.type = 'sine';
    // Smooth sweep between tones (not instant switch)
    const period = 1.2;
    for (let i = 0; i < 50; i++) {
      const t = now + i * period;
      siren.frequency.setValueAtTime(440, t);
      siren.frequency.linearRampToValueAtTime(660, t + period * 0.4);
      siren.frequency.setValueAtTime(660, t + period * 0.5);
      siren.frequency.linearRampToValueAtTime(440, t + period * 0.9);
    }

    // Wavering instability
    const wobble = ctx.createOscillator();
    wobble.frequency.value = 3;
    const wobbleG = ctx.createGain();
    wobbleG.gain.value = 6;
    wobble.connect(wobbleG).connect(siren.frequency);
    wobble.start(now);
    allNodes.push(siren, wobble);

    // Siren output — LOUDER
    const sirenG = ctx.createGain();
    sirenG.gain.value = 0.25;
    siren.connect(sirenG);
    this.toSE(sirenG);
    allGains.push(sirenG);
    siren.start(now);

    // === Second harmonic (adds body/presence) ===
    const harm = ctx.createOscillator();
    harm.type = 'sine';
    for (let i = 0; i < 50; i++) {
      const t = now + i * period;
      harm.frequency.setValueAtTime(880, t);
      harm.frequency.linearRampToValueAtTime(1320, t + period * 0.4);
      harm.frequency.setValueAtTime(1320, t + period * 0.5);
      harm.frequency.linearRampToValueAtTime(880, t + period * 0.9);
    }
    const harmG = ctx.createGain();
    harmG.gain.value = 0.08;
    harm.connect(harmG);
    this.toSE(harmG);
    harm.start(now);
    allNodes.push(harm);
    allGains.push(harmG);

    // === Pulsing beep layer (rapid alert tone) ===
    const beep = ctx.createOscillator();
    beep.type = 'square';
    beep.frequency.value = 1000;
    const beepPulse = ctx.createOscillator();
    beepPulse.type = 'square';
    beepPulse.frequency.value = 2.5; // 2.5Hz on-off
    const beepPG = ctx.createGain();
    beepPG.gain.value = 0.12;
    beepPulse.connect(beepPG);
    const beepG = ctx.createGain();
    beepG.gain.value = 0;
    beepPG.connect(beepG.gain);
    const beepFilt = ctx.createBiquadFilter();
    beepFilt.type = 'bandpass';
    beepFilt.frequency.value = 1000;
    beepFilt.Q.value = 5;
    beep.connect(beepFilt).connect(beepG);
    this.toSE(beepG);
    beep.start(now);
    beepPulse.start(now);
    allNodes.push(beep, beepPulse);
    allGains.push(beepG);

    // === Low warning horn (background throb) ===
    const horn = ctx.createOscillator();
    horn.type = 'sawtooth';
    horn.frequency.value = 110;
    const hornFilt = ctx.createBiquadFilter();
    hornFilt.type = 'lowpass';
    hornFilt.frequency.value = 300;
    // Throb (slow pulse)
    const hornLFO = ctx.createOscillator();
    hornLFO.frequency.value = 0.8;
    const hornLFOG = ctx.createGain();
    hornLFOG.gain.value = 0.06;
    hornLFO.connect(hornLFOG);
    const hornG = ctx.createGain();
    hornG.gain.value = 0.06;
    hornLFOG.connect(hornG.gain);
    horn.connect(hornFilt).connect(hornG);
    this.toSE(hornG);
    horn.start(now);
    hornLFO.start(now);
    allNodes.push(horn, hornLFO);
    allGains.push(hornG);

    return {
      stop: () => {
        const t = ctx.currentTime;
        for (const g of allGains) {
          g.gain.linearRampToValueAtTime(0, t + 1.0);
        }
        setTimeout(() => {
          for (const n of allNodes) try { n.stop(); } catch {}
        }, 1200);
      },
    };
  }

  // ==================== BGM ====================

  /** Gameplay BGM — tense action ambience (louder than tutorial) */
  startGameplayBGM(): void {
    const ctx = this.ensure();
    const now = ctx.currentTime;
    const nodes: OscillatorNode[] = [];
    const gains: GainNode[] = [];

    // Layer 1: Low drone (restrained)
    const drone = ctx.createOscillator();
    drone.type = 'sawtooth';
    drone.frequency.value = 48;
    const droneF = ctx.createBiquadFilter();
    droneF.type = 'lowpass'; droneF.frequency.value = 140;
    const droneG = ctx.createGain();
    droneG.gain.value = 0.18;
    drone.connect(droneF).connect(droneG).connect(this.bgmGain!);
    nodes.push(drone); gains.push(droneG);

    // Layer 2: Tritone dissonance (tension)
    const detune = ctx.createOscillator();
    detune.type = 'sine';
    detune.frequency.value = 67.9; // tritone from 48
    const detuneG = ctx.createGain();
    detuneG.gain.value = 0.06;
    detune.connect(detuneG).connect(this.bgmGain!);
    nodes.push(detune); gains.push(detuneG);

    // Layer 3: Slow pulse (heartbeat-like tension)
    const pulse = ctx.createOscillator();
    pulse.type = 'sine';
    pulse.frequency.value = 36;
    const pulseLFO = ctx.createOscillator();
    pulseLFO.frequency.value = 0.6;
    const pulseLFOG = ctx.createGain();
    pulseLFOG.gain.value = 0.08;
    pulseLFO.connect(pulseLFOG);
    const pulseG = ctx.createGain();
    pulseG.gain.value = 0.08;
    pulseLFOG.connect(pulseG.gain);
    pulse.connect(pulseG).connect(this.bgmGain!);
    nodes.push(pulse, pulseLFO); gains.push(pulseG);

    // Layer 4: High eerie tone (quiet, barely there)
    const whistle = ctx.createOscillator();
    whistle.type = 'sine';
    whistle.frequency.value = 660;
    const whistleLFO = ctx.createOscillator();
    whistleLFO.frequency.value = 0.12;
    const wLFOG = ctx.createGain();
    wLFOG.gain.value = 30;
    whistleLFO.connect(wLFOG).connect(whistle.frequency);
    const whistleF = ctx.createBiquadFilter();
    whistleF.type = 'bandpass'; whistleF.frequency.value = 660; whistleF.Q.value = 15;
    const whistleG = ctx.createGain();
    whistleG.gain.value = 0.03;
    whistle.connect(whistleF).connect(whistleG).connect(this.bgmGain!);
    nodes.push(whistle, whistleLFO); gains.push(whistleG);

    for (const n of nodes) n.start(now);

    // Water drips (short plop, no pitch sweep)
    const dripInterval = setInterval(() => {
      if (!this.ctx || this.ctx.state === 'closed') { clearInterval(dripInterval); return; }
      const t = this.ctx.currentTime;
      const n = this.noise(0.025, 0.04);
      const f = ctx.createBiquadFilter();
      f.type = 'bandpass'; f.frequency.value = 1200 + Math.random() * 800; f.Q.value = 3;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.05, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.03);
      n.connect(f).connect(g).connect(this.bgmGain!);
      n.start(t); n.stop(t + 0.03);
    }, 3000 + Math.random() * 5000);

    // Metal creaks (quiet, distant)
    const creakInterval = setInterval(() => {
      if (!this.ctx || this.ctx.state === 'closed') { clearInterval(creakInterval); return; }
      const t = this.ctx.currentTime;
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(80 + Math.random() * 40, t);
      osc.frequency.linearRampToValueAtTime(60 + Math.random() * 30, t + 0.4);
      const f = ctx.createBiquadFilter();
      f.type = 'bandpass'; f.frequency.value = 300 + Math.random() * 200; f.Q.value = 8;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.04, t + 0.1);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
      osc.connect(f).connect(g).connect(this.bgmGain!);
      osc.start(t); osc.stop(t + 0.5);
    }, 6000 + Math.random() * 10000);

    this.activeBgm = {
      stop: () => {
        clearInterval(dripInterval);
        clearInterval(creakInterval);
        const t = ctx.currentTime;
        for (const g of gains) g.gain.linearRampToValueAtTime(0, t + 1.5);
        setTimeout(() => {
          for (const n of nodes) try { n.stop(); } catch {}
        }, 1800);
      },
    };
  }

  /** Tension rise — swelling dissonance */
  playTensionRise(): void {
    const ctx = this.ensure();
    const now = ctx.currentTime;
    const dur = 2.5;

    // Rising dissonant cluster
    for (const freq of [90, 95, 143]) {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(freq, now);
      osc.frequency.exponentialRampToValueAtTime(freq * 4, now + dur);
      const f = ctx.createBiquadFilter();
      f.type = 'lowpass';
      f.frequency.setValueAtTime(200, now);
      f.frequency.exponentialRampToValueAtTime(3000, now + dur);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.12, now + dur * 0.85);
      g.gain.exponentialRampToValueAtTime(0.001, now + dur);
      osc.connect(f).connect(g);
      this.toSE(g);
      osc.start(now);
      osc.stop(now + dur);
    }
  }

  /** Heartbeat — realistic lub-dub with body resonance */
  playHeartbeat(count = 4): void {
    const ctx = this.ensure();
    const now = ctx.currentTime;
    for (let i = 0; i < count; i++) {
      const rate = 0.65 + i * 0.03; // slightly accelerating
      const bt = now + i * rate;

      // Lub (S1 — mitral/tricuspid)
      const lub = ctx.createOscillator();
      lub.type = 'sine';
      lub.frequency.setValueAtTime(55, bt);
      lub.frequency.exponentialRampToValueAtTime(25, bt + 0.08);
      const lg = ctx.createGain();
      lg.gain.setValueAtTime(0.4, bt);
      lg.gain.exponentialRampToValueAtTime(0.001, bt + 0.1);
      lub.connect(lg);
      this.toSE(lg);
      lub.start(bt); lub.stop(bt + 0.12);

      // Body resonance (chest cavity)
      const res = ctx.createOscillator();
      res.type = 'sine';
      res.frequency.value = 35;
      const rg = ctx.createGain();
      rg.gain.setValueAtTime(0.2, bt);
      rg.gain.exponentialRampToValueAtTime(0.001, bt + 0.15);
      res.connect(rg);
      this.toSE(rg);
      res.start(bt); res.stop(bt + 0.15);

      // Dub (S2 — aortic/pulmonic, slightly higher)
      const dub = ctx.createOscillator();
      dub.type = 'sine';
      dub.frequency.setValueAtTime(70, bt + 0.12);
      dub.frequency.exponentialRampToValueAtTime(30, bt + 0.2);
      const dg = ctx.createGain();
      dg.gain.setValueAtTime(0.25, bt + 0.12);
      dg.gain.exponentialRampToValueAtTime(0.001, bt + 0.22);
      dub.connect(dg);
      this.toSE(dg);
      dub.start(bt + 0.12); dub.stop(bt + 0.24);
    }
  }

  /** Tutorial BGM — quiet tension, silence between scares */
  startTutorialBGM(): void {
    const ctx = this.ensure();
    const now = ctx.currentTime;
    const nodes: OscillatorNode[] = [];
    const gains: GainNode[] = [];

    // Layer 1: Barely audible sub drone (felt more than heard)
    const drone = ctx.createOscillator();
    drone.type = 'sine';
    drone.frequency.value = 38;
    const droneG = ctx.createGain();
    droneG.gain.value = 0.18;
    drone.connect(droneG).connect(this.bgmGain!);
    nodes.push(drone); gains.push(droneG);

    // Layer 2: Dissonant interval (tritone — devil's interval)
    const tritone = ctx.createOscillator();
    tritone.type = 'sine';
    tritone.frequency.value = 53.7; // tritone from 38Hz
    const triG = ctx.createGain();
    triG.gain.value = 0.08;
    tritone.connect(triG).connect(this.bgmGain!);
    nodes.push(tritone); gains.push(triG);

    // Layer 3: Slow breathing LFO on volume (creates tension through silence)
    const breathOsc = ctx.createOscillator();
    breathOsc.type = 'triangle';
    breathOsc.frequency.value = 55;
    const breathLFO = ctx.createOscillator();
    breathLFO.frequency.value = 0.08; // very slow — 12s cycle
    const breathLFOG = ctx.createGain();
    breathLFOG.gain.value = 0.06;
    breathLFO.connect(breathLFOG);
    const breathG = ctx.createGain();
    breathG.gain.value = 0.0; // LFO controls it entirely — goes from silence to audible
    breathLFOG.connect(breathG.gain);
    breathOsc.connect(breathG).connect(this.bgmGain!);
    nodes.push(breathOsc, breathLFO); gains.push(breathG);

    for (const n of nodes) n.start(now);

    const intervals: ReturnType<typeof setInterval>[] = [];

    // Sudden quiet sounds from random directions (startle in silence)
    // Water drip — infrequent, breaks the silence
    intervals.push(setInterval(() => {
      if (!this.ctx || this.ctx.state === 'closed') return;
      const t = this.ctx.currentTime;
      const n = this.noise(0.02, 0.04);
      const f = ctx.createBiquadFilter();
      f.type = 'bandpass'; f.frequency.value = 1000 + Math.random() * 600; f.Q.value = 4;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.08, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.03);
      n.connect(f).connect(g).connect(this.bgmGain!);
      n.start(t); n.stop(t + 0.03);
    }, 4000 + Math.random() * 6000));

    // Distant metal clang — sudden, unsettling
    intervals.push(setInterval(() => {
      if (!this.ctx || this.ctx.state === 'closed') return;
      const t = this.ctx.currentTime;
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.value = 200 + Math.random() * 150;
      const f = ctx.createBiquadFilter();
      f.type = 'bandpass'; f.frequency.value = 300; f.Q.value = 12;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.1, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
      osc.connect(f).connect(g).connect(this.bgmGain!);
      osc.start(t); osc.stop(t + 0.35);
    }, 7000 + Math.random() * 12000));

    // Very quiet footstep-like sound (is someone there?)
    intervals.push(setInterval(() => {
      if (!this.ctx || this.ctx.state === 'closed') return;
      const t = this.ctx.currentTime;
      const n = this.noise(0.04, 0.06);
      const f = ctx.createBiquadFilter();
      f.type = 'lowpass'; f.frequency.value = 400;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.06, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
      n.connect(f).connect(g).connect(this.bgmGain!);
      n.start(t); n.stop(t + 0.05);
    }, 10000 + Math.random() * 15000));

    // Low sub rumble (building shuddering)
    intervals.push(setInterval(() => {
      if (!this.ctx || this.ctx.state === 'closed') return;
      const t = this.ctx.currentTime;
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = 22 + Math.random() * 10;
      const f = ctx.createBiquadFilter();
      f.type = 'lowpass'; f.frequency.value = 60;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.12, t + 0.8);
      g.gain.exponentialRampToValueAtTime(0.001, t + 2.0);
      osc.connect(f).connect(g).connect(this.bgmGain!);
      osc.start(t); osc.stop(t + 2.0);
    }, 12000 + Math.random() * 15000));

    this.activeBgm = {
      stop: () => {
        for (const iv of intervals) clearInterval(iv);
        const t = ctx.currentTime;
        for (const g of gains) g.gain.linearRampToValueAtTime(0, t + 1);
        setTimeout(() => {
          for (const n of nodes) try { n.stop(); } catch {}
        }, 1200);
      },
    };
  }

  stopBGM(): void {
    if (this.activeBgm) {
      this.activeBgm.stop();
      this.activeBgm = null;
    }
  }

  dispose(): void {
    this.stopBGM();
    if (this.ctx) {
      this.ctx.close();
      this.ctx = null;
    }
  }
}

/** Singleton instance */
export const audioManager = new AudioManager();
